import * as Sentry from '@sentry/tanstackstart-react';
import {
  ConnectionState,
  DisconnectReason,
  LocalAudioTrack,
  LocalVideoTrack,
  Room,
  RoomEvent,
  Track,
  type LocalTrack,
  type RemoteTrack,
  type RemoteTrackPublication,
} from 'livekit-client';
import { useCallback, useEffect, useRef, useState } from 'react';

import { hangupCall } from '#features/calls/model/calls';
import { useCallStore } from '#features/calls/model/callStore';
import { queryMicPermission } from '#features/media/micPermission';
import { setOwnedCall } from '#features/calls/utils/callOwnership';
import { pickPipSpotlight, type PipBinding } from '#features/calls/utils/pipSpotlight';
import {
  isScreenShareSupported,
  screenShareConstraints,
  userActivationState,
} from '#features/calls/utils/screenShareCapture';
import {
  classifyScreenShareFailure,
  describeScreenShareFailure,
  type ScreenShareHint,
} from '#features/calls/utils/screenShareFailure';
import { useQualityReporter } from '#features/quality';

export type CallConnectionState = 'idle' | 'connecting' | 'connected' | 'reconnecting';

export type MediaDeviceOption = { deviceId: string; label: string };

export type CallDevices = {
  microphones: MediaDeviceOption[];
  speakers: MediaDeviceOption[];
  cameras: MediaDeviceOption[];
  activeMicrophoneId: string | null;
  activeSpeakerId: string | null;
  activeCameraId: string | null;
  /** setSinkId is unsupported on iOS/older Safari — hide the speaker picker there. */
  speakerSelectionSupported: boolean;
};

/** A peer that dropped (refresh, network blip) gets this long to come back before we end the call. */
const PEER_GONE_GRACE_MS = 25_000;

/**
 * How long the screen picker may stay open before we hand the button back. Safari
 * can leave getDisplayMedia hanging forever (screen recording blocked at the OS
 * level, or the recovery window right after a cancelled share), and without a
 * deadline the share button dies until the page reloads.
 */
const SCREEN_PICKER_TIMEOUT_MS = 30_000;

const toOptions = (devices: MediaDeviceInfo[], fallbackLabel: string): MediaDeviceOption[] =>
  devices
    .filter((device) => device.deviceId)
    .map((device, index) => ({
      deviceId: device.deviceId,
      label: device.label || `${fallbackLabel} ${index + 1}`,
    }));

/**
 * Owns the LiveKit Room lifecycle for the active call. LiveKit's SDK handles all
 * SDP/ICE/TURN and automatic reconnects; this hook only connects/disconnects,
 * attaches tracks to the overlay's media elements, mirrors mic/cam toggles and
 * exposes device selection.
 */
export const useCallRoom = () => {
  const phase = useCallStore((s) => s.phase);
  const call = useCallStore((s) => s.call);
  const micEnabled = useCallStore((s) => s.micEnabled);
  const camEnabled = useCallStore((s) => s.camEnabled);
  // Collapsing into the mini pill unmounts every <video> element; re-run the
  // late-attach effects when the window comes back.
  const windowMode = useCallStore((s) => s.windowMode);

  const roomRef = useRef<Room | null>(null);
  const peerGoneTimerRef = useRef<number | null>(null);
  // One PERMANENT element per stream (peer camera / peer screen / my camera):
  // every track attaches to its own element once and stays attached, the tile
  // layout is pure CSS — no attach races when tiles swap or the area mounts late.
  const remoteCameraRef = useRef<HTMLVideoElement | null>(null);
  const remoteScreenRef = useRef<HTMLVideoElement | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const localScreenRef = useRef<HTMLVideoElement | null>(null);
  // The video AREA mounts lazily (only when someone publishes), so a track can
  // arrive BEFORE its <video> element exists — attach would silently no-op and
  // the viewer would stare at a black box. Remember the latest tracks and
  // re-attach once the elements appear (see the effects below).
  const remoteCameraTrackRef = useRef<RemoteTrack | null>(null);
  const remoteScreenTrackRef = useRef<RemoteTrack | null>(null);
  const localVideoTrackRef = useRef<LocalTrack | null>(null);
  const localScreenTrackRef = useRef<LocalTrack | null>(null);
  // Track currently bound to the PiP window's <video> (see attachPipVideo).
  const pipTrackRef = useRef<Track | null>(null);

  const [connectionState, setConnectionState] = useState<CallConnectionState>('idle');
  const [remoteJoined, setRemoteJoined] = useState(false);
  const [remoteVideoOn, setRemoteVideoOn] = useState(false);
  const [remoteScreenOn, setRemoteScreenOn] = useState(false);
  const [remoteMicOn, setRemoteMicOn] = useState(true);
  const [localSpeaking, setLocalSpeaking] = useState(false);
  const [remoteSpeaking, setRemoteSpeaking] = useState(false);
  const [peerGone, setPeerGone] = useState(false);
  const [screenSharing, setScreenSharing] = useState(false);
  const [screenSharePending, setScreenSharePending] = useState(false);
  const [screenShareHint, setScreenShareHint] = useState<ScreenShareHint | null>(null);
  // Guards the toggle itself: a second click while the picker is open must not
  // start a second capture request (that is what wedged Safari in the first place).
  const screenShareBusyRef = useRef(false);
  const [mediaError, setMediaError] = useState(false);
  /**
   * Камере отказали отдельно от микрофона. Общий флаг для обоих устройств вешал метку на обе
   * кнопки разом: человек, разрешивший микрофон и запретивший камеру, видел предупреждение и там,
   * где всё в порядке, — и переставал верить обоим.
   */
  const [camDenied, setCamDenied] = useState(false);
  /** Браузер отказался проигрывать звук без жеста — человек в звонке, но не слышит ничего. */
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [devices, setDevices] = useState<CallDevices>({
    microphones: [],
    speakers: [],
    cameras: [],
    activeMicrophoneId: null,
    activeSpeakerId: null,
    activeCameraId: null,
    speakerSelectionSupported:
      typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype,
  });

  const callId = call?.callId ?? null;

  // Телеметрия связи. Считается всегда и молча: то, что LiveKit и так сообщает браузеру, не
  // должно стоить человеку ни одного тапа.
  const quality = useQualityReporter('CALL');
  const token = call?.token ?? null;
  const livekitUrl = call?.livekitUrl ?? null;
  const shouldConnect = phase === 'active' && Boolean(token) && Boolean(livekitUrl);

  const refreshDevices = useCallback(async () => {
    try {
      // Enumerating videoinput asks the browser for the camera when the labels
      // come back empty (LiveKit calls getUserMedia to de-anonymise the list):
      // a voice-only caller would face a permission prompt out of nowhere, and a
      // machine without a camera throws outright. Ask only when the camera is live.
      const camLive = useCallStore.getState().camEnabled;
      const [microphones, speakers, cameras] = await Promise.allSettled([
        Room.getLocalDevices('audioinput'),
        Room.getLocalDevices('audiooutput'),
        Room.getLocalDevices('videoinput', camLive),
      ]);
      const room = roomRef.current;
      // Settled one by one on purpose: with Promise.all a single rejection —
      // typically the camera on a machine that has none — left every picker
      // reading "No devices found", microphone and speaker included.
      const listed = (
        result: PromiseSettledResult<MediaDeviceInfo[]>,
        fallbackLabel: string,
        previous: MediaDeviceOption[]
      ) => (result.status === 'fulfilled' ? toOptions(result.value, fallbackLabel) : previous);
      setDevices((prev) => ({
        ...prev,
        microphones: listed(microphones, 'Microphone', prev.microphones),
        speakers: listed(speakers, 'Speaker', prev.speakers),
        cameras: listed(cameras, 'Camera', prev.cameras),
        activeMicrophoneId: room?.getActiveDevice('audioinput') ?? prev.activeMicrophoneId,
        activeSpeakerId: room?.getActiveDevice('audiooutput') ?? prev.activeSpeakerId,
        activeCameraId: room?.getActiveDevice('videoinput') ?? prev.activeCameraId,
      }));
    } catch {
      // Device enumeration unavailable (insecure context, no mediaDevices) —
      // keep whatever the pickers already show.
    }
  }, []);

  /**
   * Включить звук по прямому нажатию. Именно нажатие и нужно: Safari разрешает воспроизведение
   * только из пользовательского жеста, и обещание, отклонённое здесь, — это не сбой приложения,
   * а «человек нажал не туда». Флаг снимет `AudioPlaybackStatusChanged`, когда звук пойдёт.
   */
  const unblockAudio = useCallback(async () => {
    try {
      await roomRef.current?.startAudio();
      setAudioBlocked(false);
    } catch {
      // Остаётся заблокированным — кнопка не исчезает, попробует ещё раз.
    }
  }, []);

  const switchDevice = useCallback(
    async (kind: 'audioinput' | 'audiooutput' | 'videoinput', deviceId: string) => {
      const room = roomRef.current;
      if (!room) {
        return;
      }
      try {
        await room.switchActiveDevice(kind, deviceId);
        setDevices((prev) => ({
          ...prev,
          activeMicrophoneId: kind === 'audioinput' ? deviceId : prev.activeMicrophoneId,
          activeSpeakerId: kind === 'audiooutput' ? deviceId : prev.activeSpeakerId,
          activeCameraId: kind === 'videoinput' ? deviceId : prev.activeCameraId,
        }));
      } catch {
        // Switch failed (device unplugged?) — refresh the lists.
        void refreshDevices();
      }
    },
    [refreshDevices]
  );

  useEffect(() => {
    if (!shouldConnect || !token || !livekitUrl) {
      return;
    }

    let disposed = false;
    // pauseVideoInBackground defaults to true: LiveKit tells the server to stop
    // sending video the moment the tab is hidden. That is exactly when the PiP
    // widget is on screen, so the widget would freeze to black — keep it off.
    const room = new Room({
      adaptiveStream: { pauseVideoInBackground: false },
      dynacast: true,
    });
    roomRef.current = room;
    setOwnedCall(callId);
    setConnectionState('connecting');
    setMediaError(false);
    setCamDenied(false);
    // A share attempt cannot outlive the room it belonged to.
    screenShareBusyRef.current = false;
    setScreenSharePending(false);
    setScreenShareHint(null);

    const clearPeerGoneTimer = () => {
      if (peerGoneTimerRef.current !== null) {
        window.clearTimeout(peerGoneTimerRef.current);
        peerGoneTimerRef.current = null;
      }
    };

    /**
     * «Собеседника нет» — с подписью на экране и с концом разговора, если он так и не появился.
     *
     * Заводится и когда участник ушёл, и когда его не было с самого начала: комната, в которой мы
     * одни, — это не разговор. Раньше второй случай не обрабатывался вовсе, и клиент висел в
     * пустой комнате бесконечно, каждой перезагрузкой поднимая её заново — именно так звонок
     * простоял в ACCEPTED несколько часов и заблокировал чат обоим (прод, 17.08.2026).
     */
    const armPeerGoneTimer = () => {
      setPeerGone(true);
      clearPeerGoneTimer();
      peerGoneTimerRef.current = window.setTimeout(() => {
        const state = useCallStore.getState();
        if (state.phase === 'active' && state.call) {
          hangupCall(state.call.callId).catch(() => undefined);
          state.setEnded('peer_left');
        }
      }, PEER_GONE_GRACE_MS);
    };

    const attachRemoteTrack = (track: RemoteTrack, publication?: RemoteTrackPublication) => {
      if (track.kind === Track.Kind.Video) {
        const isScreen = publication?.source === Track.Source.ScreenShare;
        const trackRef = isScreen ? remoteScreenTrackRef : remoteCameraTrackRef;
        const elRef = isScreen ? remoteScreenRef : remoteCameraRef;
        trackRef.current = track;
        if (elRef.current) {
          track.attach(elRef.current);
        }
        if (isScreen) {
          setRemoteScreenOn(true);
        } else {
          setRemoteVideoOn(true);
        }
      } else if (track.kind === Track.Kind.Audio && remoteAudioRef.current) {
        track.attach(remoteAudioRef.current);
      }
    };

    const attachLocalVideo = () => {
      room.localParticipant.videoTrackPublications.forEach((publication) => {
        if (!publication.track) {
          return;
        }
        if (publication.source === Track.Source.Camera) {
          localVideoTrackRef.current = publication.track;
          if (localVideoRef.current) {
            publication.track.attach(localVideoRef.current);
          }
        } else if (publication.source === Track.Source.ScreenShare) {
          // Self-preview of the shared screen ("what am I broadcasting").
          localScreenTrackRef.current = publication.track;
          if (localScreenRef.current) {
            publication.track.attach(localScreenRef.current);
          }
        }
      });
    };

    room
      .on(RoomEvent.TrackSubscribed, (track, publication) => {
        attachRemoteTrack(track, publication);
      })
      .on(RoomEvent.TrackUnsubscribed, (track, publication) => {
        track.detach();
        if (pipTrackRef.current === track) {
          pipTrackRef.current = null; // detach() above already dropped the PiP element
        }
        if (track.kind === Track.Kind.Video) {
          if (publication.source === Track.Source.ScreenShare) {
            if (remoteScreenTrackRef.current === track) {
              remoteScreenTrackRef.current = null;
            }
            setRemoteScreenOn(false);
          } else {
            if (remoteCameraTrackRef.current === track) {
              remoteCameraTrackRef.current = null;
            }
            setRemoteVideoOn(false);
          }
        }
      })
      .on(RoomEvent.TrackMuted, (publication, participant) => {
        if (participant.isLocal) {
          return;
        }
        if (publication.source === Track.Source.Camera) {
          setRemoteVideoOn(false);
        } else if (publication.source === Track.Source.Microphone) {
          setRemoteMicOn(false);
        }
      })
      .on(RoomEvent.TrackUnmuted, (publication, participant) => {
        if (participant.isLocal) {
          return;
        }
        if (publication.source === Track.Source.Camera) {
          setRemoteVideoOn(true);
        } else if (publication.source === Track.Source.Microphone) {
          setRemoteMicOn(true);
        }
      })
      .on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
        // Discord-style speaking rings around the participant avatars.
        const localSpeaks = speakers.some((speaker) => speaker.isLocal);
        setLocalSpeaking(localSpeaks);
        setRemoteSpeaking(speakers.some((speaker) => !speaker.isLocal));
        if (localSpeaks) {
          // «Человек за весь звонок не сказал ни слова» — единственный признак мёртвого
          // микрофона, который вообще не виден по сетевым метрикам.
          quality.tracker.onSpoke();
        }
      })
      .on(RoomEvent.LocalTrackPublished, (publication) => {
        if (publication.source === Track.Source.Camera) {
          attachLocalVideo();
        }
        if (publication.source === Track.Source.ScreenShare) {
          attachLocalVideo();
          setScreenSharing(true);
        }
      })
      .on(RoomEvent.LocalTrackUnpublished, (publication) => {
        if (publication.source === Track.Source.ScreenShare) {
          localScreenTrackRef.current = null;
          setScreenSharing(false);
        }
      })
      .on(RoomEvent.ParticipantConnected, () => {
        clearPeerGoneTimer();
        setPeerGone(false);
        setRemoteJoined(true);
      })
      .on(RoomEvent.ParticipantDisconnected, () => {
        // Собеседник переехал на другое своё устройство, а не ушёл: он входит в комнату заново, и
        // LiveKit выбивает прежнее соединение — нам приходит «участник отключился» уже ПОСЛЕ того,
        // как новый подключился, поэтому `ParticipantConnected` успевает погасить таймер раньше,
        // чем тот заведён. Через 25 секунд звонок закрывался у второй стороны, хотя человек всё
        // это время сидел в комнате (прод, 17.08.2026). Пока в комнате кто-то есть — разговор жив.
        if (room.remoteParticipants.size > 0) {
          return;
        }

        // The peer dropped — could be a page refresh or a network blip, so give
        // them a grace window to come back before finishing the call for real.
        setRemoteVideoOn(false);
        setRemoteScreenOn(false);
        armPeerGoneTimer();
      })
      .on(RoomEvent.MediaDevicesChanged, () => {
        void refreshDevices();
      })
      .on(RoomEvent.ActiveDeviceChanged, () => {
        void refreshDevices();
      })
      // Качество связи никуда не показывается в 1:1 — подписка нужна только ради телеметрии.
      // Чужие деления человек всё равно не чинит, поэтому считаем своё.
      .on(RoomEvent.ConnectionQualityChanged, (connectionQuality, participant) => {
        if (participant.isLocal) {
          quality.tracker.onQuality(connectionQuality);
        }
      })
      .on(RoomEvent.ConnectionStateChanged, (state) => {
        quality.tracker.onConnectionState(state);
        if (state === ConnectionState.Connected) {
          setConnectionState('connected');
        } else if (state === ConnectionState.Reconnecting) {
          setConnectionState('reconnecting');
        } else if (state === ConnectionState.Connecting) {
          setConnectionState('connecting');
        }
      })
      .on(RoomEvent.AudioPlaybackStatusChanged, () => {
        // Playback got blocked (iOS after an interruption requires a gesture) —
        // resume on the next tap anywhere.
        if (!room.canPlaybackAudio) {
          // Человек в этот момент не слышит НИЧЕГО и не понимает почему, а сеть при этом
          // идеальна. Без отдельного флага такой звонок выглядит в отчёте здоровым.
          quality.tracker.onPlaybackBlocked();
          // Ждать случайного тапа мало: у человека в Safari блокировка повторялась пять раз за
          // разговор, и он всё это время сидел в тишине, не понимая, что нажать (прод, 17.08.2026).
          // Поэтому ещё и говорим об этом прямо — кнопкой в окне звонка.
          setAudioBlocked(true);
          document.addEventListener(
            'pointerdown',
            () => {
              room.startAudio().catch(() => undefined);
            },
            { once: true }
          );
        } else {
          setAudioBlocked(false);
        }
      })
      .on(RoomEvent.Disconnected, (reason) => {
        if (disposed) {
          // Комната уже разобрана, а сводка отправлена: причина от мертвеца легла бы на
          // следующий звонок того же человека.
          return;
        }
        quality.tracker.onDisconnected(reason);
        const state = useCallStore.getState();
        if (reason === DisconnectReason.DUPLICATE_IDENTITY) {
          // The call moved to another tab of the same user — close quietly.
          state.reset();
          return;
        }
        if (reason === DisconnectReason.CLIENT_INITIATED) {
          return; // our own disconnect() — the main flow handles the state
        }
        if (state.phase === 'active') {
          state.setEnded('disconnected');
          // Tell the server: if both clients die without this, the row stays
          // ACCEPTED for hours and blocks new calls for both users.
          if (callId) {
            hangupCall(callId).catch(() => undefined);
          }
        }
      });

    const connect = async () => {
      try {
        await room.connect(livekitUrl, token);
        if (disposed) {
          return;
        }
        setRemoteJoined(room.remoteParticipants.size > 0);
        if (room.remoteParticipants.size === 0) {
          // Вошли в комнату, а там никого. Для звонящего это норма на первые секунды, поэтому
          // ждём то же окно, что и на уход собеседника, — но именно ждём, а не остаёмся здесь
          // навсегда.
          armPeerGoneTimer();
        }
        room.remoteParticipants.forEach((participant) => {
          participant.trackPublications.forEach((publication) => {
            if (publication.track) {
              attachRemoteTrack(publication.track as RemoteTrack, publication);
            }
          });
        });

        const { micEnabled: mic, camEnabled: cam } = useCallStore.getState();
        // Микрофон и камера включаются РАЗДЕЛЬНО. В одном `try` отказ камеры помечал заодно и
        // микрофон: человек, разрешивший звук и запретивший видео, получал метку «нет доступа» на
        // обеих кнопках — и переставал верить обеим.
        try {
          await room.localParticipant.setMicrophoneEnabled(mic);
        } catch {
          // getUserMedia denied — stay in the call audio-less, surface a hint.
          setMediaError(true);
          // Same signal into telemetry: without it a denied microphone is indistinguishable
          // from someone who simply stayed quiet, and both land as `spoke: false`.
          quality.tracker.onMicDenied();
        }
        if (cam) {
          try {
            await room.localParticipant.setCameraEnabled(true);
          } catch {
            setCamDenied(true);
            useCallStore.getState().setCamEnabled(false);
          }
        }
        attachLocalVideo();
        void refreshDevices();
      } catch (error) {
        if (!disposed) {
          const state = useCallStore.getState();
          // Немой catch здесь означал, что отказ подключения к комнате не виден нигде: человек
          // получал «звонок не удался», а в Sentry не было ни строки, и различить «медиасервер
          // недоступен», «токен просрочен» и «сеть у человека» было нечем. Репортим осознанно —
          // уровнем warning и с тегами, потому что чаще всего это чужая сеть, а не наш сбой.
          Sentry.withScope((scope) => {
            scope.setTag('scope', 'callRoom');
            scope.setTag('type', 'livekit-connect');
            scope.setLevel('warning');
            scope.setContext('call', {
              callId,
              livekitUrl,
              online: typeof navigator === 'undefined' ? undefined : navigator.onLine,
              connectionState: room.state,
            });
            Sentry.captureException(error);
          });
          hangupCall(callId ?? '').catch(() => undefined);
          state.setEnded('connect_failed');
        }
      }
    };

    quality.setTarget(callId);

    void connect();

    return () => {
      disposed = true;
      // Причину ставим сами и до отправки: событие Disconnected прилетит уже после disconnect(),
      // то есть после снимка, и любой сброс трубки уезжал бы в отчёт без причины вовсе.
      // Настоящую причину эта строка не затирает — в накопителе побеждает первая.
      quality.tracker.onDisconnected(DisconnectReason.CLIENT_INITIATED);
      quality.flush();
      setOwnedCall(null);
      clearPeerGoneTimer();
      remoteCameraTrackRef.current = null;
      remoteScreenTrackRef.current = null;
      localVideoTrackRef.current = null;
      localScreenTrackRef.current = null;
      pipTrackRef.current = null;
      roomRef.current = null;
      setConnectionState('idle');
      setRemoteJoined(false);
      setRemoteVideoOn(false);
      setRemoteScreenOn(false);
      setRemoteMicOn(true);
      setLocalSpeaking(false);
      setRemoteSpeaking(false);
      setPeerGone(false);
      setScreenSharing(false);
      screenShareBusyRef.current = false;
      setScreenSharePending(false);
      setScreenShareHint(null);
      void room.disconnect();
    };
    // Reconnect only when the call identity/credentials change, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldConnect, callId, token, livekitUrl]);

  /**
   * Раннее предупреждение об отсутствующем разрешении: если микрофон запрещён насовсем, человек
   * узнаёт об этом на входе в звонок, а не когда собеседник уже ждёт ответа. Верим только ответу
   * `denied` — почему именно так, написано в {@link queryMicPermission}.
   */
  useEffect(() => {
    if (!shouldConnect) {
      return;
    }
    let cancelled = false;
    const warnIfDenied = async () => {
      if ((await queryMicPermission()) === 'denied' && !cancelled) {
        setMediaError(true);
      }
    };
    void warnIfDenied();
    return () => {
      cancelled = true;
    };
  }, [shouldConnect]);

  // Mirror mic/cam toggles into the live room.
  useEffect(() => {
    const room = roomRef.current;
    if (!room || phase !== 'active') {
      return;
    }
    room.localParticipant.setMicrophoneEnabled(micEnabled).catch(() => {
      // Немой catch здесь стоил человеку разговора: он жал размьют посреди звонка, браузер
      // отказывал, а кнопка продолжала гореть «включено» — и о том, что его не слышат, он узнавал
      // от собеседника (прод 23.08.2026: 107 секунд в разговоре с мёртвым микрофоном). Отказ на
      // ВКЛЮЧЕНИИ обязан быть виден: подсказкой, честной кнопкой и строкой в телеметрии — иначе
      // в отчёте он неотличим от человека, который просто молчал.
      if (!micEnabled) {
        return;
      }
      setMediaError(true);
      quality.tracker.onMicDenied();
      useCallStore.getState().setMicEnabled(false);
    });
  }, [micEnabled, phase, quality.tracker]);

  useEffect(() => {
    const room = roomRef.current;
    if (!room || phase !== 'active') {
      return;
    }
    room.localParticipant.setCameraEnabled(camEnabled).catch(() => {
      // То же и с камерой: выключить запрещённую всегда получается, поэтому реагируем только на
      // неудавшееся включение — иначе предупреждение снималось бы само при первом же выключении.
      if (!camEnabled) {
        return;
      }
      setCamDenied(true);
      useCallStore.getState().setCamEnabled(false);
    });
  }, [camEnabled, phase]);

  // iOS audio-session interruption: another app grabbing the mic (dictation, a
  // native call, a game) mutes or kills our tracks and suspends playback, and
  // Safari never restores them on its own. When the tab becomes visible again,
  // restart dead local tracks and resume audio playback.
  useEffect(() => {
    if (phase !== 'active') {
      return;
    }
    const restoreMedia = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      const room = roomRef.current;
      if (!room) {
        return;
      }
      const micTrack = room.localParticipant.getTrackPublication(Track.Source.Microphone)?.track;
      if (micTrack instanceof LocalAudioTrack) {
        const raw = micTrack.mediaStreamTrack;
        if (raw.readyState === 'ended' || raw.muted) {
          micTrack.restartTrack().catch(() => undefined);
        }
      }
      const camTrack = room.localParticipant.getTrackPublication(Track.Source.Camera)?.track;
      if (camTrack instanceof LocalVideoTrack && camTrack.mediaStreamTrack.readyState === 'ended') {
        camTrack.restartTrack().catch(() => undefined);
      }
      if (!room.canPlaybackAudio) {
        room.startAudio().catch(() => undefined);
      }
    };
    document.addEventListener('visibilitychange', restoreMedia);
    window.addEventListener('focus', restoreMedia);
    return () => {
      document.removeEventListener('visibilitychange', restoreMedia);
      window.removeEventListener('focus', restoreMedia);
    };
  }, [phase]);

  // Late attach: the video area (and its <video> elements) mounts only when
  // someone publishes — bind the remembered tracks once the elements exist.
  useEffect(() => {
    if (remoteVideoOn && remoteCameraRef.current && remoteCameraTrackRef.current) {
      remoteCameraTrackRef.current.attach(remoteCameraRef.current);
    }
    if (remoteScreenOn && remoteScreenRef.current && remoteScreenTrackRef.current) {
      remoteScreenTrackRef.current.attach(remoteScreenRef.current);
    }
  }, [remoteVideoOn, remoteScreenOn, windowMode]);

  useEffect(() => {
    if (!camEnabled) {
      return;
    }
    const el = localVideoRef.current;
    const track = localVideoTrackRef.current;
    if (el && track) {
      track.attach(el);
    }
  }, [camEnabled, remoteVideoOn, remoteScreenOn, windowMode]);

  useEffect(() => {
    if (!screenSharing) {
      return;
    }
    const el = localScreenRef.current;
    const track = localScreenTrackRef.current;
    if (el && track) {
      track.attach(el);
    }
  }, [screenSharing, windowMode]);

  /**
   * Binds the spotlight stream to the PiP window's <video> and returns what got
   * bound (null — nothing live, the widget shows the avatar). Priority mirrors
   * the in-app stage: peer screen > peer camera > my screen > my camera.
   *
   * Two reasons this attaches the real LiveKit track instead of copying
   * `srcObject`: the SDK only counts an element as visible-in-PiP when the track
   * itself is attached to a node living inside `documentPictureInPicture.window`
   * (otherwise adaptive stream sees a hidden tab and cuts the stream), and the
   * requested resolution is derived from the attached elements' size — a widget
   * fed by a 0×0 hidden element would get the lowest layer.
   *
   * Reads refs only (no state), so the PiP polling loop never sees a stale closure.
   */
  const attachPipVideo = useCallback((element: HTMLVideoElement): PipBinding | null => {
    const next = pickPipSpotlight({
      remoteScreen: remoteScreenTrackRef.current,
      remoteCamera: remoteCameraTrackRef.current,
      localScreen: localScreenTrackRef.current,
      localCamera: localVideoTrackRef.current,
    });

    if (pipTrackRef.current && pipTrackRef.current !== next?.track) {
      pipTrackRef.current.detach(element);
      pipTrackRef.current = null;
    }
    if (next?.track && pipTrackRef.current !== next.track) {
      next.track.attach(element);
      pipTrackRef.current = next.track;
    }
    return next?.binding ?? null;
  }, []);

  /** Unbind before the PiP window (and its <video>) goes away. */
  const detachPipVideo = useCallback((element: HTMLVideoElement) => {
    pipTrackRef.current?.detach(element);
    pipTrackRef.current = null;
  }, []);

  /**
   * Starts/stops the screen share by hand instead of calling `setScreenShareEnabled`.
   *
   * The SDK marks the source as "publishing" BEFORE it calls getDisplayMedia and
   * only clears that mark once the call settles. A getDisplayMedia that never
   * settles — Safari does exactly that when screen recording is blocked at the OS
   * level — leaves the source pending forever, and from then on every further call
   * spends 10s inside `waitForPendingPublicationOfSource` and resolves with
   * `undefined`: no throw, no track, no state change. The button looks dead and
   * only a page reload brings it back. Owning the capture here means a timeout can
   * release it, and the SDK never gets into that state at all.
   */
  const toggleScreenShare = useCallback(async () => {
    const room = roomRef.current;
    if (!room || screenShareBusyRef.current) {
      return;
    }
    if (screenSharing) {
      screenShareBusyRef.current = true;
      setScreenSharePending(true);
      setScreenShareHint(null);
      try {
        const publications = [
          room.localParticipant.getTrackPublication(Track.Source.ScreenShare),
          room.localParticipant.getTrackPublication(Track.Source.ScreenShareAudio),
        ];
        await Promise.all(
          publications.map((publication) =>
            publication?.track
              ? room.localParticipant.unpublishTrack(publication.track, true)
              : undefined
          )
        );
      } finally {
        screenShareBusyRef.current = false;
        setScreenSharePending(false);
      }
      return;
    }

    /**
     * Запрос экрана — самое первое, что делает обработчик, и зовём мы его сами, а не через SDK.
     *
     * `getDisplayMedia` — единственный медиа-запрос, которому нужен «живой жест»: микрофон и
     * камеру браузер отдаёт и без него, поэтому работающая камера ничего не доказывает. Пока
     * между нажатием и вызовом стоял слой библиотеки, Safari отказывал молча — не показывая окно
     * выбора вовсе, — а так выглядит именно потерянная активация, а не отказ человека.
     */
    // Браузер без захвата экрана отсеиваем до всего остального: обращение к отсутствующему
    // методу бросает синхронно, мимо разбора ниже, и обработчик падает целиком.
    if (!isScreenShareSupported()) {
      setScreenShareHint('failed');
      Sentry.captureMessage('call: screen share unsupported by browser', 'warning');
      return;
    }

    const constraints = screenShareConstraints(navigator.userAgent);
    const askedAt = Date.now();
    const activation = userActivationState();

    screenShareBusyRef.current = true;
    setScreenSharePending(true);
    setScreenShareHint(null);
    let captured: MediaStreamTrack[] = [];
    // Объявлена снаружи, а присваивается внутри try: сам вызов тоже должен быть под защитой,
    // иначе синхронный бросок снова уйдёт мимо catch.
    let capture: Promise<MediaStream> | null = null;

    try {
      capture = navigator.mediaDevices.getDisplayMedia(constraints);
      const stream = await Promise.race([
        capture,
        new Promise<null>((resolve) => {
          window.setTimeout(() => resolve(null), SCREEN_PICKER_TIMEOUT_MS);
        }),
      ]);

      if (!stream) {
        // The picker never answered. Stop whatever shows up later, otherwise the OS
        // keeps capturing a screen nobody publishes.
        void capture
          ?.then((late) => late.getTracks().forEach((track) => track.stop()))
          .catch(() => undefined);
        setScreenShareHint('permission');
        Sentry.captureMessage('call: screen picker never answered', 'warning');
        return;
      }

      captured = stream.getTracks();
      const [video] = stream.getVideoTracks();
      if (!video) {
        throw new Error('screen capture returned no video track');
      }
      // Источник задаём явно — по нему собеседник отличает демонстрацию от камеры, а SDK
      // выбирает профиль кодирования для статичной картинки.
      await room.localParticipant.publishTrack(video, { source: Track.Source.ScreenShare });
      captured = [];
    } catch (error) {
      // Publishing blew up half-way: stop every captured track, or the OS keeps
      // recording a screen nobody receives. Stopping a published one ends it,
      // which the SDK turns into an unpublish of its own accord.
      captured.forEach((track) => track.stop());
      setScreenShareHint(classifyScreenShareFailure(error));
      Sentry.captureException(error, {
        tags: { feature: 'call-screen-share' },
        extra: describeScreenShareFailure(error, Date.now() - askedAt, activation),
      });
    } finally {
      screenShareBusyRef.current = false;
      setScreenSharePending(false);
    }
  }, [screenSharing]);

  /**
   * Попросить доступ к устройству по нажатию на кнопку с меткой.
   *
   * <p>До этого нажатие на запрещённый микрофон просто пыталось его включить: браузер отказывал
   * молча, кнопка гасла обратно, и человек оставался ровно там же, где был. Запрос через
   * `getUserMedia` показывает системное окно — единственный способ вернуть доступ, не уходя в
   * настройки. Отказ здесь не ошибка: человек вправе нажать «Блокировать», метка тогда остаётся.
   */
  const requestMicAccess = useCallback(async (): Promise<boolean> => {
    const room = roomRef.current;
    try {
      if (room && phase === 'active') {
        await room.localParticipant.setMicrophoneEnabled(true);
        useCallStore.getState().setMicEnabled(true);
      } else {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Поток нужен был только ради вопроса о разрешении; оставленный включённым, он зажигает
        // индикатор записи у человека, который ещё не начал говорить.
        stream.getTracks().forEach((track) => track.stop());
      }
      setMediaError(false);
      void refreshDevices();
      return true;
    } catch {
      return false;
    }
  }, [phase, refreshDevices]);

  const requestCameraAccess = useCallback(async (): Promise<boolean> => {
    const room = roomRef.current;
    try {
      if (room && phase === 'active') {
        await room.localParticipant.setCameraEnabled(true);
        useCallStore.getState().setCamEnabled(true);
      } else {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach((track) => track.stop());
      }
      setCamDenied(false);
      void refreshDevices();
      return true;
    } catch {
      return false;
    }
  }, [phase, refreshDevices]);

  return {
    remoteCameraRef,
    remoteScreenRef,
    remoteAudioRef,
    localVideoRef,
    localScreenRef,
    connectionState,
    remoteJoined,
    remoteVideoOn,
    remoteScreenOn,
    remoteMicOn,
    localSpeaking,
    remoteSpeaking,
    peerGone,
    screenSharing,
    screenSharePending,
    screenShareHint,
    mediaError,
    camDenied,
    requestMicAccess,
    requestCameraAccess,
    audioBlocked,
    unblockAudio,
    devices,
    switchDevice,
    refreshDevices,
    toggleScreenShare,
    attachPipVideo,
    detachPipVideo,
  };
};
